import { useMemo, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { Section } from "@/components/section";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { RefreshButton } from "@/components/list/refresh-button";
import { UserAvatar, UserChip } from "@/components/user-chip";
import { UserPickerDialog } from "@/components/user-selector";
import type { PickerUser } from "@/components/user-selector";
import { fetcher, putFetcher } from "@/lib/swr";
import type { User } from "@/lib/swr";
import { EditAppDialog } from "@/screens/Apps/edit-app-dialog";
import { groupAppsByGroup } from "@/constants/app-groups";
import type { App } from "@/types/domain";
import { ArchiveAppDialog } from "@/screens/Apps/archive-app-dialog";
import { refreshApps } from "@/screens/Apps/refresh-apps";
import { Tabs, TabsTrigger } from "@/components/ui/tabs";
import { TabBar } from "@/components/nav/route-tabs";

const MAX_MEMBER_AVATARS = 8;

// One app row with its admin actions (edit name/group, manage owners,
// manage members). This is the admin's own management entry — it never
// routes into /apps/:id/*, which stays visibility-scoped (admin has no
// data-plane privilege there; the management endpoints do allow admin).
const AppRow = ({
  app,
  index,
  owners,
  members,
  onMembersChanged,
}: {
  app: App;
  index: number;
  owners: User[];
  members: User[];
  onMembersChanged: () => void;
}) => {
  const { mutate } = useSWRConfig();
  const { trigger: saveOwners, isMutating: savingOwners } = useSWRMutation(
    `/apps/${app.id}/owners`,
    putFetcher,
    {
      onSuccess: () => {
        refreshApps(mutate);
      },
    }
  );
  // Members editor writes the same endpoint AppMembersDialog does, but takes
  // its current selection from the page's /users/full inversion instead of
  // GET /apps/:id/members — one fetch for the whole page, not one per row.
  const { trigger: saveMembers, isMutating: savingMembers } = useSWRMutation(
    `/apps/${app.id}/members`,
    putFetcher,
    { onSuccess: () => onMembersChanged() }
  );

  return (
    <RowCard>
      <RowIndex n={index + 1} />

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <p className="truncate text-sm font-medium leading-none">{app.name}</p>
        <span className="text-xs text-muted-foreground">#{app.id}</span>
      </div>

      {/* Owners — the accountable people; missing owner needs admin attention */}
      <div className="flex w-48 shrink-0 items-center gap-1">
        {owners.length ? (
          <div className="flex min-w-0 flex-wrap gap-1">
            {owners.map((o) => (
              <UserChip key={o.id} user={o} />
            ))}
          </div>
        ) : (
          <span className="text-xs text-destructive">No owner</span>
        )}
        <UserPickerDialog
          multiple
          selected={owners as PickerUser[]}
          onConfirm={(users) =>
            saveOwners({ ownerIds: users.map((u) => String(u.id)) })
          }
          title="Manage owners"
          description="Owners manage members and settings of this app. Keep at least one."
          buttonText={savingOwners ? "Saving…" : owners.length ? "Owners" : "Set"}
        />
      </div>

      {/* Members as stacked avatars, overflow collapsed to a count */}
      <div className="flex w-44 shrink-0 items-center justify-end gap-1">
        {members.length === 0 ? (
          <span className="text-xs text-muted-foreground">No members</span>
        ) : (
          <div className="flex items-center">
            {members.slice(0, MAX_MEMBER_AVATARS).map((m) => (
              <UserAvatar
                key={m.id}
                user={m}
                className="-ml-1.5 border-2 border-background first:ml-0"
              />
            ))}
            {members.length > MAX_MEMBER_AVATARS && (
              <span className="ml-1 text-xs tabular-nums text-muted-foreground">
                +{members.length - MAX_MEMBER_AVATARS}
              </span>
            )}
          </div>
        )}
        <UserPickerDialog
          multiple
          selected={members as PickerUser[]}
          onConfirm={(users) =>
            saveMembers({ userIds: users.map((u) => String(u.id)) })
          }
          title="Manage members"
          description={`Who can see ${app.name}. The owner always stays a member.`}
          buttonText={savingMembers ? "Saving…" : "Members"}
        />
      </div>

      <div className="flex shrink-0 gap-2">
        <EditAppDialog app={app} />
        <ArchiveAppDialog app={app} />
      </div>
    </RowCard>
  );
};

// Admin app management: every app with its owners and visibility members,
// each editable in place. Members are derived from /users/full (each user's
// visibleApps inverted) — no extra endpoint.
const AppOwners = () => {
  const [state, setState] = useState("active");
  const { data: apps, isLoading, isValidating, mutate: refresh } = useSWR<App[]>(["/apps/all"]);
  const { data: users, mutate: refreshUsers } = useSWR<User[]>(
    ["/users/full"],
    fetcher
  );

  const list: App[] = useMemo(() => (
    (apps || []).filter((app) => !!app.archived === (state === "archived"))
  ), [apps, state]);
  const membersOf = (appId: string) =>
    (users ?? []).filter((u) =>
      (u.visibleApps ?? []).some((a) => String(a.id) === String(appId))
    );
  const ownersOf = (ownerIds?: string[] | null) => {
    const ids = (ownerIds ?? []).map(String);
    return (users ?? []).filter((u) => ids.includes(String(u.id)));
  };

  const groups = useMemo(() => groupAppsByGroup(list), [list]);

  return (
    <Section
      title="Apps"
      action={<RefreshButton spinning={isValidating} onClick={() => refresh()} />}
    >
      <Tabs value={state} onValueChange={setState}>
        <TabBar>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="archived">Archived</TabsTrigger>
        </TabBar>
      </Tabs>
      <div className="flex flex-col gap-4">
        {isLoading && <ListLoading />}
        {!isLoading && list.length === 0 && <ListEmpty>No apps.</ListEmpty>}
        {groups.map(([group, groupApps]) => (
          <div key={group} className="flex flex-col gap-1">
            <p className="text-xs font-medium uppercase text-muted-foreground">
              {group}
              <span className="ml-1.5 normal-case tabular-nums">
                ({groupApps.length})
              </span>
            </p>
            {groupApps.map((app, index) => (
              <AppRow
                key={app.id}
                app={app}
                index={index}
                owners={ownersOf(app.ownerIds)}
                members={membersOf(app.id)}
                onMembersChanged={() => refreshUsers()}
              />
            ))}
          </div>
        ))}
      </div>
    </Section>
  );
};

export default AppOwners;
